/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * RelatedFieldRuleDialog — a "Show only when" rule that reads another table.
 *
 * WHY THIS EXISTS. A rule could always be written against this record's own
 * columns. That forces a rule about a related row to key on the FK's stored
 * id — `registration_type_id is 7` — and an id is an accident of insert order:
 * the same registration type is 7 in one application and 4 in the next. The
 * rule cannot be moved, and nothing on the screen says so. Reading the related
 * row's own stable code instead — `registration_type.longcode is not
 * INVITATION_ONLY` — is portable and legible, and both the client and the
 * server already evaluate exactly that: `evaluate_condition` walks a dotted
 * path, and the page ships the related row the rule names.
 *
 * WHAT IT WRITES. A plain condition, the same shape a rule on this record's own
 * column has always had:
 *
 *   { field: 'registration_type.longcode', op: 'neq', value: 'INVITATION_ONLY' }
 *
 * There is no second stored form and no flag. The only thing that makes it a
 * related rule is that the field names a table.
 *
 * WHICH TABLES ARE OFFERED. Only the ones this table's own FK columns point at
 * — the `dir: 'up'` edges. That is not a simplification: the server resolves a
 * related row by following this record's FK columns, so a rule naming any other
 * table would read nothing, fail closed, and hide the button forever with no
 * error anywhere. Offering only what can be resolved is the difference between
 * a picker and a trap.
 *
 * Edits a DRAFT. Nothing reaches the stored configuration until Save, so Cancel
 * and Escape genuinely discard.
 */

import React, { useEffect, useRef, useState } from 'react';

import { useDialogInset } from './dialogInset';
import Hint from './Hint';
import {
  CONDITION_OPS, OPS_WITH_VALUE, conditionValue,
  allowedValuesOf, unknownConditionValues,
} from './conditionOps';

/** The tables a rule on this record can actually reach. See the header. */
export function relatedChoices(relatedEdges) {
  const seen = new Set();
  return (relatedEdges || [])
    .filter(e => e && e.dir === 'up' && e.table)
    .filter(e => (seen.has(e.table) ? false : seen.add(e.table)))
    .map(e => ({
      table: e.table,
      label: e.display_name || e.table,
      via: e.via,
      fields: e.fields || [],
    }));
}

/** A stored condition -> the dialog's controls. */
function draftFrom(rule, choices) {
  const field = String((rule || {}).field || '');
  const [head, ...rest] = field.split('.');
  const known = choices.some(c => c.table === head);
  return {
    table: known ? head : '',
    column: known ? rest.join('.') : '',
    op: (rule || {}).op || 'eq',
    value: Array.isArray((rule || {}).value)
      ? (rule || {}).value.join(', ')
      : ((rule || {}).value ?? ''),
  };
}

export default function RelatedFieldRuleDialog({ rule, relatedEdges, onSave, onCancel }) {
  const choices = relatedChoices(relatedEdges);
  const [draft, setDraft] = useState(() => draftFrom(rule, choices));
  const dialogRef = useRef(null);
  const inset = useDialogInset();

  useEffect(() => {
    if (dialogRef.current) dialogRef.current.focus();
    function onKeyDown(e) { if (e.key === 'Escape') onCancel(); }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  const set = changes => setDraft(d => ({ ...d, ...changes }));

  const chosen = choices.find(c => c.table === draft.table);
  const columns = chosen ? chosen.fields : [];
  const needsValue = OPS_WITH_VALUE.includes(draft.op);
  const allowed = allowedValuesOf(columns, draft.column);
  const unknown = needsValue ? unknownConditionValues(allowed, draft.value) : [];
  // A single-select cannot express "is one of", which takes a comma-separated
  // list, so that operator keeps the text box.
  const usePicker = allowed.length > 0 && draft.op !== 'in';

  /* What stops a Save. Each is something that would store a rule the server
   * cannot resolve — and an unresolvable rule fails closed, so the button would
   * simply never appear again with nothing to say why. */
  const problems = [];
  if (!draft.table) problems.push('Choose the related table this rule reads.');
  else if (!draft.column) problems.push('Choose the field to test.');
  else if (needsValue && String(draft.value).trim() === '') {
    problems.push('Enter the value to compare against.');
  }

  function save() {
    onSave({
      field: `${draft.table}.${draft.column}`,
      op: draft.op,
      ...(needsValue ? { value: conditionValue(draft.op, draft.value) } : {}),
    });
  }

  const opLabel = (CONDITION_OPS.find(o => o.key === draft.op) || {}).label || draft.op;

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center
                 justify-content-center"
      style={{ background: 'rgba(24,22,44,.42)', zIndex: 1080, overflowY: 'auto',
               padding: '1rem',
               paddingLeft: `calc(1rem + ${inset}px)` }}
      onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="bg-white rounded-3 shadow-lg d-flex flex-column"
        role="dialog"
        aria-modal="true"
        aria-label="A field from a related table"
        tabIndex={-1}
        ref={dialogRef}
        style={{ maxWidth: 560, width: '100%', maxHeight: '86vh' }}
      >
        <div className="px-3 py-3 border-bottom d-flex justify-content-between align-items-start">
          <div>
            <div className="fw-bold">A field from a related table</div>
            <div className="small text-muted">
              Reads the row this record points at, so the rule can test a stable
              code rather than the id that happens to hold it here.
            </div>
          </div>
          <button type="button" className="btn-close" aria-label="Close" onClick={onCancel} />
        </div>

        <div className="px-3 py-3 overflow-auto">
          {choices.length === 0 ? (
            <Hint tone="warn">
              This table has no field pointing at another table, so there is no
              related row for a rule to read.
            </Hint>
          ) : (
            <>
              <div className="mb-2">
                <label className="form-label small fw-semibold mb-1" htmlFor="rel-table">
                  Related table
                </label>
                <select
                  id="rel-table" className="form-select form-select-sm"
                  value={draft.table}
                  // Changing the table clears the column: a field name from the
                  // previous table would look chosen while naming nothing.
                  onChange={e => set({ table: e.target.value, column: '', value: '' })}
                >
                  <option value="">— choose a table —</option>
                  {choices.map(c => (
                    <option key={c.table} value={c.table}>{c.label}</option>
                  ))}
                </select>
                {chosen && (
                  <Hint>
                    Reached through this record’s <code>{chosen.via}</code>.
                  </Hint>
                )}
              </div>

              <div className="mb-2">
                <label className="form-label small fw-semibold mb-1" htmlFor="rel-column">
                  Field
                </label>
                <select
                  id="rel-column" className="form-select form-select-sm"
                  value={draft.column}
                  disabled={!draft.table}
                  onChange={e => set({ column: e.target.value, value: '' })}
                >
                  <option value="">— choose a field —</option>
                  {columns.map(f => (
                    <option key={f.name} value={f.name}>
                      {f.display_name && f.display_name !== f.name
                        ? `${f.name} — ${f.display_name}`
                        : f.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="d-flex gap-2 flex-wrap align-items-center mb-2">
                <select
                  className="form-select form-select-sm" style={{ maxWidth: 150 }}
                  aria-label="Condition operator"
                  value={draft.op}
                  onChange={e => set({ op: e.target.value })}
                >
                  {CONDITION_OPS.map(o => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
                {needsValue && usePicker && (
                  <select
                    className="form-select form-select-sm" style={{ maxWidth: 240 }}
                    aria-label="Condition value"
                    value={draft.value}
                    onChange={e => set({ value: e.target.value })}
                  >
                    <option value="">— choose a value —</option>
                    {allowed.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                )}
                {needsValue && !usePicker && (
                  <input
                    className="form-control form-control-sm" style={{ maxWidth: 240 }}
                    aria-label="Condition value"
                    // Suggestions, not a restriction: a column may hold a value
                    // it has not declared, so anything typed is still accepted.
                    list={allowed.length ? 'rel-values' : undefined}
                    value={draft.value}
                    onChange={e => set({ value: e.target.value })}
                  />
                )}
                {needsValue && !usePicker && allowed.length > 0 && (
                  <datalist id="rel-values">
                    {allowed.map(v => <option key={v} value={v} />)}
                  </datalist>
                )}
              </div>

              {unknown.length > 0 && (
                <Hint tone="warn">
                  {unknown.length === 1
                    ? `“${unknown[0]}” is not a value `
                    : `“${unknown.join('”, “')}” are not values `}
                  this field holds, so the rule would never match and the button
                  would simply never appear. It holds: {allowed.join(', ')}.
                </Hint>
              )}

              {draft.table && draft.column && (
                <Hint>
                  {`Shows the button only when ${draft.table}.${draft.column} ${opLabel}`}
                  {needsValue ? ` ${draft.value}` : ''}.
                </Hint>
              )}

              {problems.length > 0 && <Hint tone="warn">{problems[0]}</Hint>}
            </>
          )}
        </div>

        <div className="px-3 py-2 border-top d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button" className="btn btn-sm btn-primary"
            disabled={problems.length > 0}
            onClick={save}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
